/**
 * Whether the sign-in screen may hand the browser straight to the identity
 * provider instead of rendering a button that has one destination.
 *
 * On web the screen offers no decision — one control, one place it can go — so
 * painting it costs a click that carries no information. Redirecting on mount
 * removes that click, and removes more than that whenever the IdP session
 * cookie is still alive: `/authorize` comes straight back with a code and the
 * sign-in costs no interaction at all.
 *
 * That is only safe while something can say *not this time*. Two arrivals at
 * `/(auth)/login` must show the button rather than bounce:
 *
 *   • **Just signed out.** `post_logout_redirect_uri` lands on `/`, the root
 *     guard sends that to `/(auth)/login`, and an unconditional redirect would
 *     put the user back at the IdP one hop after they asked to leave. Worse, in
 *     the case `endIdpSession` documents — an `id_token_hint` too old to end the
 *     session — the session is still alive, so the redirect signs them straight
 *     back in and Sign Out reads as a button that does nothing.
 *     `flow_55_logout_returns_to_app` asserts `login.idp-button` is visible at
 *     exactly this moment, which is this rule written as a test.
 *   • **A sign-in that just failed.** `app/auth/callback.tsx` offers a way back
 *     here from its error screen. Without suppression that way back is a way
 *     out to the IdP, which returns to the same failure — the loop this file
 *     exists to make impossible.
 *
 * `sessionStorage` rather than `AsyncStorage`, deliberately. The flag has to
 * survive a full page load, because logout leaves the origin entirely and comes
 * back, which rules out module state. It must also NOT outlive the tab: a flag
 * stuck in `localStorage` would disable the redirect permanently and silently,
 * with nothing on screen to say why. Closing the tab clears this one.
 *
 * ⚠️  THE FLAG IS STAMPED WITH THE PAGE LOAD THAT WROTE IT, and that is not
 *     decoration — without it logout redirects straight back out to the IdP,
 *     which `flow_55_logout_returns_to_app` caught against a real session on
 *     2026-08-29.
 *
 *     `logout()` clears `isAuthenticated` several statements before the browser
 *     actually leaves for `end_session`. That clearing makes the root guard
 *     route to `/(auth)/login`, so the sign-in screen MOUNTS — briefly, in the
 *     page load that is on its way out — and asks this module the question. A
 *     flag consumed by that transient mount is gone by the time the IdP sends
 *     the browser back, which is the one arrival it was written for.
 *
 *     So a flag bearing the CURRENT page load's stamp suppresses without being
 *     consumed: the logout it belongs to has not finished. Only a flag from a
 *     previous page load is spent, because only then has the round trip it was
 *     written for actually happened.
 */

import { Platform } from 'react-native';

const KEY = '@cynosure/suppress_auto_login';

/**
 * Memoised for the life of the page load, so the answer cannot change between
 * a render and the effect acting on it, and so React re-invoking the initialiser
 * (StrictMode does, in development) cannot consume the flag and then report the
 * opposite. It is deliberately resolved on FIRST CALL rather than at module
 * load: a cross-tab logout writes the flag while this tab is still showing the
 * app, and the sign-in screen has not mounted yet at that point.
 */
let decision: boolean | null = null;

/**
 * Identifies THIS page load. Module scope is re-evaluated by a full navigation,
 * so a value that merely has to differ from the previous load's needs no more
 * than this.
 */
let pageLoadId = Math.random().toString(36).slice(2);

/**
 * Record that the next arrival at the sign-in screen must render the button.
 * Single-use — consumed by the arrival it was written for.
 */
export function suppressAutoLogin(): void {
  if (Platform.OS !== 'web') return;
  try {
    window.sessionStorage.setItem(KEY, pageLoadId);
  } catch {
    // Private modes and blocked site data throw on access, not just on quota.
    // Losing the flag costs an unwanted redirect; throwing here would break
    // logout itself, which is the worse of the two.
  }
}

/**
 * Whether the sign-in screen should redirect on mount rather than paint.
 *
 * Reading CLEARS the suppression, so one logout suppresses one arrival and not
 * every later sign-in in the same tab.
 */
export function shouldAutoLogin(): boolean {
  if (Platform.OS !== 'web') return false;
  if (decision !== null) return decision;

  let suppressed = false;
  try {
    const stamp = window.sessionStorage.getItem(KEY);
    suppressed = stamp !== null;
    // Left in place when this page load wrote it: the logout that wrote it is
    // still in flight, and the arrival it is for has not happened yet. Spending
    // it here is the bug described at the top of this file.
    if (suppressed && stamp !== pageLoadId) window.sessionStorage.removeItem(KEY);
  } catch {
    // Unreadable storage cannot be distinguished from an absent flag, and the
    // redirect is the behaviour the screen exists for, so fall through to it.
    suppressed = false;
  }

  decision = !suppressed;
  return decision;
}

/**
 * Test seam: stands in for the module re-evaluation a real page load performs.
 * Not used by app code.
 *
 * It must roll the page-load id as well as the memo. A helper that reset only
 * the memo would leave the id matching, so the flag would never be spent and
 * every test would read as "suppressed forever" — which is the opposite of the
 * bug being guarded against, and would hide it.
 */
export function simulateNewPageLoad(): void {
  decision = null;
  pageLoadId = Math.random().toString(36).slice(2);
}
